import {
  FontColorEnum,
  FormField,
  FormHint,
  FormInput,
  FormLabel,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  MessageInfoFilledIcon,
  Tooltip,
} from '@acceso/design-system';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TInputTextProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
};

export function InputText<T extends FieldValues>({
  form,
  name,
  required,
  disabled,
  label,
  placeholder,
  tooltip,
}: TInputTextProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';

  return (
    <FormField>
      {label && (
        <FormLabel htmlFor={name} className="flex items-center">
          {label}
          {labelSuffix}

          {tooltip && (
            <Tooltip help={tooltip} grow>
              <IconButton size={IconButtonSizeEnum.SM} color={IconButtonColorEnum.NONE_DEFAULT}>
                <MessageInfoFilledIcon
                  size={IconSizeEnum.MD}
                  className="text-accent-active-primary-30"
                />
              </IconButton>
            </Tooltip>
          )}
        </FormLabel>
      )}
      <Controller
        disabled={disabled}
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <FormInput
              disabled={disabled}
              value={value}
              placeholder={placeholder || label}
              onChange={e => {
                onChange(e);
              }}
              hasError={!!error}
            />
            {error && (
              <FormHint color={FontColorEnum.Error20} type="error">
                {error.message}
              </FormHint>
            )}
          </>
        )}
      />
    </FormField>
  );
}
